// TermsOfService.jsx
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './App.css';

export default function TermsOfService() {
    useEffect(() => {
        window.scrollTo(0, 0)
        document.title = 'Terms of Service | QRmor';
        const metaDesc = document.querySelector('meta[name="description"]');
        const content = 'Terms of Service for QRmor, the non-clonable, anti-counterfeit QR code authentication platform from Scinova Scientifics.';
        if (metaDesc) {
            metaDesc.setAttribute('content', content);
        } else {
            const tag = document.createElement('meta');
            tag.name = 'description';
            tag.content = content;
            document.head.appendChild(tag);
        }
    }, [])
    return (
        <>
            <header>
                <nav className="wrap">
                    <div className="brand">
                        <img src="/assets/QRmor.png" alt="QRmor logo" />
                    </div>
                    <Link to="/" className="btn btn-primary btn-sm">Back to home</Link>
                </nav>
            </header>

            <section>
                <div className="wrap legal-content" style={{ maxWidth: 860, padding: '48px 32px 80px' }}>
                    <span className="eyebrow">Legal</span>
                    <h1 style={{ fontSize: 'clamp(28px, 4vw, 40px)', marginTop: 12, marginBottom: 8 }}>
                        Terms of Use and Legal Clauses
                    </h1>
                    <p style={{ color: 'var(--ink-soft)', fontSize: 14, marginBottom: 36 }}>
                        Last updated: August 2026
                    </p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                        <div>
                            <h2 style={{ marginBottom: 4 }}>1. Terms of Use</h2>
                            <ul style={{ color: 'var(--ink-soft)', marginTop: 8, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 14, textAlign: 'justify' }}>
                                <li>
                                    <strong>Acceptance of Terms.</strong> By accessing, browsing, downloading,
                                    integrating, or utilizing the software platform, associated mobile
                                    applications, APIs, or physical anti-counterfeiting verification tags
                                    (collectively, the "Service"), you explicitly acknowledge that you have
                                    read, understood, and agreed to be legally bound by these Terms of Use.
                                </li>
                                <li>
                                    <strong>Commercial Purpose &amp; Eligibility.</strong> The Service is
                                    engineered exclusively as an enterprise brand protection tool. We give you
                                    enough opportunities to test the QRmor codes to suit your requirements in
                                    the form of demos and sample QRmor codes. You acknowledge that you have
                                    internally tested the sample QRmor codes on all performance parameters as
                                    per your requirements at your enterprise and got satisfied with its
                                    performance and results and only then opted for QRmor services. By
                                    deploying QRmor codes, you warrant that you are a legally recognized
                                    corporate entity or registered commercial business executing these terms
                                    at arm's length for lawful verification workflows.
                                </li>
                                <li>
                                    <strong>Printing of QRmor codes.</strong> Printing of QRmor codes is in
                                    your scope and you are solely responsible for its outcome. QRmor or
                                    Scinova Scientifics or <a href="https://ciirc.res.in" target="_blank" rel="noopener noreferrer">CIIRC</a> shall not be responsible for any eventuality
                                    arising out of printing of QRmor codes on your packaging material.
                                </li>
                                <li>
                                    <strong>Prohibited Conduct &amp; Anti-Reverse Engineering.</strong> Users
                                    shall not, directly or indirectly: (a) attempt to clone, replicate, forge,
                                    duplicate, or bypass the security integrity of the proprietary
                                    non-clonable deep-tech properties within the QRmor secure codes; (b)
                                    reverse engineer, decompile, or disassemble the proprietary algorithms
                                    underlying the verification loops; or (c) use the Service to build a
                                    competing anti-counterfeiting tracking tool.
                                </li>
                                <li>
                                    <strong>Compliance with Data Regulations.</strong> To the extent the
                                    Service processes scanning telemetries, end-user device strings, or
                                    geo-location indicators to flag physical product fraud, you warrant
                                    absolute compliance with the Digital Personal Data Protection (DPDP) Act,
                                    2023, the Information Technology Act, 2000, and related rules, assuring
                                    necessary end-user legal consents are duly obtained.
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h2 style={{ marginBottom: 4 }}>2. Intellectual Property (IP) Ownership</h2>
                            <ul style={{ color: 'var(--ink-soft)', marginTop: 8, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 14, textAlign: 'justify' }}>
                                <li>
                                    <strong>Exclusive Proprietary Retainment.</strong> Scinova Scientifics and
                                    <a href="https://ciirc.res.in" target="_blank" rel="noopener noreferrer">CIIRC</a> (or its authorized operating parent/subsidiary entities, collectively
                                    referred to as "QRmor") retain absolute, undivided, and exclusive
                                    ownership, title, and intellectual property rights in and to the Service.
                                    This includes, without limitation, all source code, software
                                    architectures, non-clonable features of QRmor QR codes, generation and
                                    verification algorithms, user interface layouts, documentation, and
                                    registered trademarks.
                                </li>
                                <li>
                                    <strong>Industrial Trade Secrets.</strong> The unique mathematical and
                                    physical mechanisms used to distinguish original QRmor codes from cloned
                                    duplications constitute critical proprietary trade secrets protected under
                                    Indian common law and global patent structures. No implied license, title
                                    transfer, or technology hand-off is executed under these website terms.
                                </li>
                                <li>
                                    <strong>Telemetry Data License.</strong> While the enterprise client
                                    retains title to its distinct branding assets, product lines, and specific
                                    customer registry inputs, the client hereby grants QRmor an unrestricted,
                                    perpetual, royalty-free, irrevocable, worldwide license to utilize
                                    anonymized, aggregated system telemetry (e.g., historical scan counts,
                                    regional fraud hot-spots, and verification success rates) to maintain and
                                    upgrade platform threat intelligence networks.
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h2 style={{ marginBottom: 4 }}>3. Limitation of Liability</h2>
                            <ul style={{ color: 'var(--ink-soft)', marginTop: 8, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 14, textAlign: 'justify' }}>
                                <li>
                                    <strong>As-Is and As-Available Baseline.</strong> The platform, deep-tech
                                    tags, and verification services are delivered strictly on an "as-is" and
                                    "as-available" baseline. QRmor disclaims all legal representations,
                                    statutory conditions, and warranties, whether explicit or implied,
                                    including the implied warranties of merchantability, up-time performance,
                                    or fitness for an enterprise's specific operational environment.
                                </li>
                                <li>
                                    <strong>No Absolute Immunity Guarantee.</strong> While QRmor utilizes
                                    high-level multi-layered security engineering to flag illicit
                                    duplications, the client explicitly recognizes that no digital or physical
                                    anti-counterfeiting tracking tool is 100% immune to highly sophisticated,
                                    any kind of cyberattacks, cyberwarfare, virus attack, server failure, power
                                    outage, internet connectivity failure, physical degradation of prints,
                                    extreme environment variations, or malicious hardware bypasses. QRmor does
                                    not warrant zero undetected bypasses.
                                </li>
                                <li>
                                    <strong>Exclusion of Indirect Business Damages.</strong> To the highest
                                    scope sanctioned under applicable Indian statutes, QRmor, its developers,
                                    research partners (including <a href="https://ciirc.res.in" target="_blank" rel="noopener noreferrer">CIIRC</a> and Scinova Scientifics), sister
                                    concerns, directors, employees, interns, investors, advisors, promotors,
                                    service agents, auditors, contractors and affiliates shall never be held
                                    liable for any indirect, incidental, special, collateral, consequential,
                                    punitive or exemplary commercial damages. This structural carve-out
                                    encompasses loss of business profits, loss of goodwill, loss of savings, loss of royalty,
                                    loss of corporate reputation, loss of corporate image, loss of brand reputation, loss of brand image, loss of brand
                                    equity, loss of corporate revenue deficits, loss of supply chain disruptions, loss of legal
                                    disputes, loss of brand dilution, loss of costs associated with sweeping physical product
                                    recalls, material loss, production loss, production time loss or system
                                    downtime loss.
                                </li>
                                <li>
                                    <strong>Maximum Financial Cap.</strong> The cumulative aggregate financial
                                    liability of QRmor for any and all civil claims, contractual breaches, tort
                                    liabilities, or indemnities originating under this agreement shall be
                                    strictly capped at and limited to the total net fees paid by the respective
                                    enterprise client to QRmor during the trailing three (3) months or
                                    prorated to three (3) months if the fee is paid annually, immediately
                                    preceding the formal written notification of the event giving rise to
                                    liability. If there was no fee paid prior to three months of the written
                                    notification, there is no financial liability applicable for QRmor.
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h2 style={{ marginBottom: 4 }}>4. Governing Law &amp; Jurisdiction</h2>
                            <ul style={{ color: 'var(--ink-soft)', marginTop: 8, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 14, textAlign: 'justify' }}>
                                <li>
                                    <strong>Governing Law.</strong> These Terms of Use, their validity,
                                    construction, performance, and any non-contractual obligations or disputes
                                    stemming from them shall be exclusively governed by and interpreted in
                                    accordance with the substantive laws of the Republic of India, without
                                    regard to conflict of law rules.
                                </li>
                                <li>
                                    <strong>Exclusive Ahmedabad Forum Selection.</strong> The parties
                                    explicitly agree that any legal dispute, judicial action, lawsuit, or
                                    structural litigation arising out of or connecting back to this website
                                    framework, product outages, or verification errors shall be filed strictly
                                    and exclusively in the competent courts located in Ahmedabad, Gujarat,
                                    India. Both parties hereby waive any objections regarding inconvenient
                                    forum principles or alternative domestic venues.
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h2 style={{ marginBottom: 4 }}>5. Contact</h2>
                            <p style={{ color: 'var(--ink-soft)', marginTop: 8 }}>
                                Questions about these Terms, contact us at{' '}
                                <a href="mailto:contact@qrmor.com" style={{ color: 'var(--crimson-deep)', fontWeight: 700 }}>
                                    contact@qrmor.com
                                </a>{' '}
                                or call{' '}
                                <a href="tel:+919974051262" style={{ color: 'var(--crimson-deep)', fontWeight: 700 }}>
                                    +91-9974051262
                                </a>.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}